import { useEffect, useRef, useState } from "react";
import classNames from "classnames";

import { formatSearchString } from "src/utils/funcs";

export const CListboxMulti = ({
  value = [],
  options,
  isOptionDisabled,
  onChange,
  onChangeCallback,
}) => {
  //#region Data
  const [search, setSearch] = useState("");

  const filteredOptions = search
    ? options.filter((e) =>
        formatSearchString(e.label).includes(formatSearchString(search)),
      )
    : options;

  const inputRef = useRef(null);
  const listRef = useRef(null);
  //#endregion

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  //#region Events
  const handleToggleOption = (chosenOption) => {
    const currentValues = value || [];
    const isSelected = currentValues.includes(chosenOption.value);

    let newValues;
    if (isSelected) {
      // Bỏ chọn
      newValues = currentValues.filter((v) => v !== chosenOption.value);
    } else {
      // Chọn thêm
      newValues = [...currentValues, chosenOption.value];
    }

    const selectedOptions = options.filter((opt) =>
      newValues.includes(opt.value),
    );

    onChange?.(newValues, selectedOptions);
    onChangeCallback?.(newValues, selectedOptions);

    // Không đóng popup khi chọn, để user có thể chọn nhiều items
  };

  const handleSelectAll = () => {
    const selectableOptions = filteredOptions.filter(
      (opt) => !isOptionDisabled?.(opt),
    );
    const allValues = selectableOptions.map((opt) => opt.value);
    const newValues = Array.from(new Set([...(value || []), ...allValues]));

    const selectedOptions = options.filter((opt) =>
      newValues.includes(opt.value),
    );

    onChange?.(newValues, selectedOptions);
    onChangeCallback?.(newValues, selectedOptions);
  };
  //#endregion

  //#region Render
  return (
    <>
      {/* Search Input with Select All Button */}
      <div className="flex gap-2 px-2 pt-2 pb-1 items-center">
        <input
          ref={inputRef}
          type="text"
          placeholder="Tìm kiếm..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ border: "1px solid transparent" }}
          className={classNames(
            "flex-1 p-[6px_12px] rounded-lg font-medium tracking-wide",
            "bg-gray-50 !border-gray-200 outline-none focus-visible:!border-gray-400",
            "placeholder:text-gray-400",
          )}
        />
        <button
          type="button"
          onClick={handleSelectAll}
          title="Chọn tất cả"
          className={classNames(
            "shrink-0 px-3 py-2 rounded-lg outline-none",
            "bg-blue-500 hover:bg-blue-600 text-white",
            "transition-colors duration-150",
            "flex items-center justify-center gap-1.5 font-medium text-sm",
          )}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M5 13l4 4L19 7"></path>
          </svg>
          <span className="hidden sm:inline">Tất cả</span>
        </button>
      </div>

      {/* Listbox Items */}
      <ul
        className="max-h-64 overflow-y-auto p-1.5"
        ref={listRef}
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#d1d5db #f3f4f6",
        }}
        sx={{
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f3f4f6",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#d1d5db",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#9ca3af",
          },
        }}
      >
        {filteredOptions.length > 0 ? (
          filteredOptions.map((opt) => {
            const isDisabled = isOptionDisabled?.(opt) ?? false;
            const isSelected = value?.includes(opt.value) ?? false;

            return (
              <li
                key={opt.value}
                data-selected={isSelected}
                onClick={() => !isDisabled && handleToggleOption(opt)}
                className={classNames(
                  "px-3 py-2.5 rounded-lg cursor-pointer ",
                  "transition-all duration-150 ease-out group",
                  "flex items-center gap-3",
                  isSelected && "bg-blue-500/5 text-blue-500 font-medium",
                  !isDisabled &&
                    !isSelected &&
                    "hover:bg-gray-100 text-gray-700",
                  isDisabled && "opacity-50 cursor-default text-gray-400",
                )}
              >
                {/* Checkbox indicator */}
                <span
                  style={{ border: "1px solid transparent" }}
                  className={classNames(
                    "shrink-0 w-4 h-4 rounded",
                    "flex items-center justify-center transition-all duration-200",
                    isSelected
                      ? "!border-blue-500 bg-blue-500 text-white"
                      : "!border-gray-300 bg-white group-hover:!border-blue-500/50",
                  )}
                >
                  {isSelected && (
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M5 13l4 4L19 7"></path>
                    </svg>
                  )}
                </span>
                <span className="flex-1 truncate">{opt.label}</span>
              </li>
            );
          })
        ) : (
          <li className="px-3 py-4 text-gray-400 text-center">
            Không có kết quả
          </li>
        )}
      </ul>
    </>
  );
  //#endregion
};
